import { useTranslation } from 'react-i18next';
import { Form, Input, Switch, Tabs, Tag, TreeSelect, Typography } from 'antd';
import { PERMISSIONS } from '@tms/shared';

import { useDictionaries } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { TableCard } from '@/components/TableCard';
import { departmentTreeData } from '@/lib/departments';

import { CrudPanel } from './admin/CrudPanel';

interface PositionRow {
  id: number;
  departmentId: number;
  code: string;
  name: string;
  requiresDriverLicense: boolean;
  isActive: boolean;
  _count: { employees: number };
}

interface DepartmentRow {
  id: number;
  parentId: number | null;
  code: string;
  name: string;
  isActive: boolean;
  _count: { vehicles: number; employees: number };
}

const activeTag = (isActive: boolean) =>
  isActive ? <Tag color="green">активен</Tag> : <Tag>отключён</Tag>;

/**
 * Структура — кадровая часть платформы, поэтому отдельная страница в блоке
 * HR, а не вкладка в «Администрировании»: там живут технические справочники
 * платформы (виды топлива, модели техники и т.п.), а не кадровые данные.
 * Подразделения и должности — одна и та же работа отдела кадров, поэтому
 * собраны на одной странице вкладками, а не разнесены по разным разделам.
 */
export function PositionsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const dictionaries = useDictionaries();

  if (!can(PERMISSIONS.DICTIONARY_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр справочников')}</Typography.Text>;
  }

  const canManage = can(PERMISSIONS.DICTIONARY_MANAGE);

  return (
    <TableCard title={t('Структура')}>
      <Tabs
        items={[
          {
            key: 'departments',
            label: t('Подразделения'),
            children: (
              <CrudPanel<DepartmentRow>
                url="/dictionaries/departments"
                queryKey="departments"
                statusFilter={false}
                treeParent={(row) => row.parentId}
                title={t('Подразделение')}
                canManage={canManage}
                description={t(
                  'Подразделения принадлежат активному офису и не видны другим офисам. Вложенность задаётся полем «Вышестоящее подразделение»: фильтры по подразделению учитывают и все вложенные.',
                )}
                columns={[
                  // Наименование — первой колонкой: в ней дерево рисует отступы
                  // и стрелки раскрытия, узкая колонка кода для этого не годится.
                  { title: t('Наименование'), dataIndex: 'name' },
                  { title: t('Код'), dataIndex: 'code', width: 110 },
                  {
                    title: t('Закреплено'),
                    width: 220,
                    render: (_: unknown, row: DepartmentRow) =>
                      `${row._count.vehicles} ед. техники, ${row._count.employees} сотрудников`,
                  },
                  { title: t('Статус'), dataIndex: 'isActive', width: 110, render: activeTag },
                ]}
                formFields={(isEdit, _form, editing) => (
                  <>
                    <Form.Item name="code" label={t('Код')} rules={[{ required: true }]}>
                      <Input disabled={isEdit} placeholder="SST" />
                    </Form.Item>
                    <Form.Item name="name" label={t('Наименование')} rules={[{ required: true }]}>
                      <Input placeholder={t('Служба спецтранспорта')} />
                    </Form.Item>
                    <Form.Item
                      name="parentId"
                      label={t('Вышестоящее подразделение')}
                      tooltip={t('Пусто — подразделение верхнего уровня')}
                      // Очистка поля даёт undefined, а такое поле не уходит на
                      // сервер вовсе — подразделение не поднялось бы наверх.
                      normalize={(value: number | undefined) => value ?? null}
                    >
                      <TreeSelect
                        allowClear
                        showSearch
                        treeNodeFilterProp="title"
                        treeDefaultExpandAll
                        placeholder={t('Верхний уровень')}
                        // Само подразделение и его вложенные не предлагаются:
                        // подчинить отдел своему же сектору — значит получить цикл.
                        treeData={departmentTreeData(
                          dictionaries.data?.departments ?? [],
                          editing?.id,
                        )}
                      />
                    </Form.Item>
                    {isEdit && (
                      <Form.Item name="isActive" label={t('Активно')} valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    )}
                  </>
                )}
              />
            ),
          },
          {
            key: 'positions',
            label: t('Должности'),
            children: (
              <CrudPanel<PositionRow>
                url="/dictionaries/positions"
                queryKey="positions"
                statusFilter={false}
                title={t('Должность')}
                canManage={canManage}
                description={t(
                  'Список должностей свой у каждого подразделения: механик в службе спецтранспорта и кладовщик на складе — разные записи. Отдельная галочка решает, предполагает ли должность управление транспортом — такой сотрудник появится на странице «Водители».',
                )}
                columns={[
                  {
                    title: t('Подразделение'),
                    dataIndex: 'departmentId',
                    width: 200,
                    render: (value: number) =>
                      dictionaries.data?.departments.find((d) => d.id === value)?.name ?? '—',
                  },
                  { title: t('Код'), dataIndex: 'code', width: 140 },
                  { title: t('Наименование'), dataIndex: 'name' },
                  {
                    title: t('Водительская'),
                    dataIndex: 'requiresDriverLicense',
                    width: 120,
                    render: (value: boolean) => (value ? <Tag color="blue">{t('Да')}</Tag> : '—'),
                  },
                  {
                    title: t('Используется'),
                    width: 130,
                    align: 'right',
                    render: (_: unknown, row: PositionRow) => row._count.employees,
                  },
                  { title: t('Статус'), dataIndex: 'isActive', width: 110, render: activeTag },
                ]}
                formFields={(isEdit) => (
                  <>
                    <Form.Item
                      name="departmentId"
                      label={t('Подразделение')}
                      rules={[{ required: true, message: t('Обязательное поле') }]}
                    >
                      <TreeSelect
                        disabled={isEdit}
                        showSearch
                        treeNodeFilterProp="title"
                        treeDefaultExpandAll
                        treeData={departmentTreeData(dictionaries.data?.departments ?? [])}
                      />
                    </Form.Item>
                    <Form.Item
                      name="code"
                      label={t('Код')}
                      tooltip={t('После создания не меняется: на него ссылаются выгрузки')}
                      rules={[
                        { required: true, message: t('Обязательное поле') },
                        {
                          pattern: /^[A-Z0-9-]+$/,
                          message: t('Заглавные латинские буквы, цифры и дефис'),
                        },
                      ]}
                    >
                      <Input disabled={isEdit} placeholder="FORKLIFT" />
                    </Form.Item>
                    <Form.Item name="name" label={t('Наименование')} rules={[{ required: true }]}>
                      <Input placeholder={t('Водитель вилочного погрузчика')} />
                    </Form.Item>
                    <Form.Item
                      name="requiresDriverLicense"
                      label={t('Предполагает управление транспортом')}
                      valuePropName="checked"
                      tooltip={t('Сотрудник с такой должностью появится на странице «Водители»')}
                    >
                      <Switch />
                    </Form.Item>
                    {isEdit && (
                      <Form.Item name="isActive" label={t('Активна')} valuePropName="checked">
                        <Switch />
                      </Form.Item>
                    )}
                  </>
                )}
              />
            ),
          },
        ]}
      />
    </TableCard>
  );
}
